import "./style.scss";

import { FC, useContext, useState } from "react";
import { LinkAuthenticationElement, PaymentElement, AddressElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { sendEmailAfterPayment, sendErrorPayment } from "../../api";

import Button from "shared/ui/Button";
import { Button_Style } from "shared/ui/Button/Button";
import MoneyDisplay from "components/MoneyDisplay";
import { PaymentTypes, isBasicAccompanimentOffer } from "shared/config/pricings";
import { UserContext } from "features/account";
import { extractNumber } from "utils/utils";
import {createAccompaniment} from "features/accompaniment";
import { useNavigate } from "react-router-dom";

interface PaymentStripFormProps {
    title: any;
    steps?: any;
    link: any;
    setDatas: (data: any) => void;
    datas: any;
    handlePreviousStep: () => void;
    handlePaymentResult: (title: string, message: string, paymentSucceeded: boolean) => void;
    isMonth?: boolean;
}

const PaymentStripForm: FC<PaymentStripFormProps> = ({
    title,
    setDatas,
    datas,
    steps,
    link,
    isMonth,
    handlePreviousStep,
    handlePaymentResult,
}) => {
    const navigate = useNavigate();
    const { userType } = useContext(UserContext);

    const stripe = useStripe();
    const elements = useElements();

    const [isProcessing, setIsProcessing] = useState(false);
    const [textProcessing, setTextProcessing] = useState("Chargement...");
    const [email, setEmail] = useState("");

    let linkAuthenticationElement = elements?.getElement("linkAuthentication");

    linkAuthenticationElement?.on("change", (event) => {});

    const handleSubmit = async (e: any) => {
        setIsProcessing(true);
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        if (!email) {
            setIsProcessing(false);
            return;
        }

        const body = new FormData();
        body.append("type", userType || "individual");

        body.append("firstname", datas.user?.firstname ?? "client")
        body.append("customerEmail", datas.user?.email);
        body.append("email", email);

        // Append additional PaymentLog-related from database fields
        body.append("title", datas.chosenPlan?.title ?? "Untitled");
        body.append("head", datas?.chosenPlan?.head ?? "No head");
        body.append("text", datas?.chosenPlan?.text ?? "");
        body.append("isRecurringPayment", datas?.chosenPlan?.isRecurringPayment?.toString() ?? "false");

        if (datas.chosenPlan?.head == PaymentTypes.CREDIT) {
            // On extrait le nombre de credits acheté
            body.append("credits", extractNumber(datas.chosenPlan?.title)!.toString());
        }
        else if (datas.chosenPlan?.head == PaymentTypes.ACCOMAPGNEMENT) {
            body.append("description", datas?.chosenPlan?.description ?? "");
            body.append("accompagnementType", datas.chosenPlan?.title);
        }
        else if (datas.chosenPlan?.head == PaymentTypes.ID_TRACKER) {
            body.append("subscriptionType", datas.chosenPlan?.title);
            body.append("offreId", datas.chosenPlan?.offreId);
            body.append("typePrelevement", `${isMonth ? "mensuel" : "annuel"}`);
        }
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/${link}`,
            },
            redirect: "if_required",
        });

        let nextTitle = `souscription annulée`;
        let message = `Le paiement a été annulé`;
        let isOK = false;

        if (paymentIntent && paymentIntent.status === "succeeded") {
            // Abonnement : rien à créer ici. L'abonnement existe déjà (créé par
            // /subscription_intent AVANT l'affichage du formulaire) et le paiement
            // qui vient d'aboutir est celui de sa première facture. Le rappel à
            // create_customer/create_subscription qui se trouvait ici doublait
            // l'encaissement du jour puis décalait l'échéance d'un an.
            body.append("amount", (paymentIntent?.amount! * 0.01).toString());
            body.append("transactionId", paymentIntent?.id);

            await sendEmailAfterPayment(body, datas.chosenPlan?.head);

            if (datas.chosenPlan?.head == PaymentTypes.ACCOMAPGNEMENT) {
                const data: any = {
                    type: userType ?? 'individual',
                    documents_ids: datas?.documents_ids,
                    adresse: datas?.addresse,
                    city: datas?.city,
                    postalCode: datas?.postalCode,
                    comment: datas?.comment,
                    filesToUpload: datas?.filesToUpload, // si il choisi le doc depui IDTRacker
                    docNumber: datas.docNumber,
                    docCodeCountry: datas.docCodeCountry,
                    typeDoc: datas.typeDoc,
                    raisons: datas.raisons,
                    plaintesRaisons: datas.plaintesRaisons,
                    docsToUpload: datas.docsToUpload,
                    birthPlace: datas.birthPlace,
                    birthDate: datas.birthDate,
                    ownerOfThisDoc: datas.ownerOfThisDoc,
                    sex: datas.sex,
                    dateDepotPlainte: datas.dateDepotPlainte,
                    chosenPlan: datas.chosenPlan,
                    alreadyPaidFromWordpress: false,
                };
                await createAccompaniment(data);
            }

            nextTitle = `PAIEMENT ACCEPTÉ`;
            message = `MERCI d’avoir souscrit.\n Vous bénéficiez de : ${title}. 
            ${datas.chosenPlan.head === PaymentTypes.ACCOMAPGNEMENT ?
              !isBasicAccompanimentOffer(datas.chosenPlan.offreId) ? '\nVos informations ont été reçues par l’équipe ID Protect, qui va désormais entamer la rédaction de votre courrier.': '\nVos courriers ont été rédigés et ajoutés à votre dossier d’accompagnement'
              : '' }
            \nUn email contenant les détails de votre paiement vous sera envoyé
            ${datas?.isNewUser ?
                '\nVos informations de connexion vous ont été envoyées par email. '
                :
                ''
            }`;
            isOK = true;
        } else if (error) {
            console.log(error);
            if (error.type == "validation_error") {
                setIsProcessing(false);
                return;
            }
            nextTitle = `PAIEMENT ANNULÉ`;
            message = `Malheureusement, votre paiement pour : ${title} n’a pas abouti. \nUn email vous sera envoyé avec plus de détails.`;
            isOK = false;

            await sendErrorPayment(body, datas.chosenPlan?.head);
        } else {
            nextTitle = `PAIEMENT ANNULÉ`;
            message = `Le paiement a été annulé`;
            isOK = false;
            await sendErrorPayment(body, datas.chosenPlan?.head);
        }

        setIsProcessing(false);
        handlePaymentResult(nextTitle, message, isOK);
    };

    return (
        <div
            id="payment-form"
            className="PaymentStripForm w-100 d-flex flex-column align-items-center justify-content-between">
            <div className="w-100 text-center text-dark">
                <h2>{title}</h2>
                <span className="font-S"> Paiement {isMonth === true ? 'Mensuel' : isMonth === false? 'Annuel' : 'une fois' }</span>
            </div>
            <MoneyDisplay
                plan={datas.chosenPlan}
                isMonth={isMonth}
            />
            <div className="h-stripe">
                <LinkAuthenticationElement
                    options={{ defaultValues: { email: datas?.user?.email ?? "" } }}
                    onChange={(event) => {
                        setEmail(event.value.email);
                    }}
                />
                {/*<AddressElement  options={{mode: "billing", display: {name: 'full'}, validation: {} }}/>*/}
                <PaymentElement />
                <br />
                <div className={`d-flex w-100 justify-content-between flex-wrap`}>
                    {steps && (
                        <Button
                            label={"Retour"}
                            onClick={() => handlePreviousStep()}
                            style={Button_Style.OUTLINED}
                        />
                    )}

                    {!steps && (
                        <Button
                            label={"Retour"}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(-1);
                            }}
                            style={Button_Style.OUTLINED}
                        />
                    )}

                    <Button
                        disabled={isProcessing}
                        onClick={handleSubmit}
                        label={`${isProcessing ? textProcessing : "PAYER"}`}
                        data-testid="payer-stripe-button"
                    />
                </div>
            </div>

        </div>
    );
};

export default PaymentStripForm;
